import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { isBoolean } from 'lodash-es';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { AsyncPipe } from '@angular/common';
import { ShIndeterminateDirective } from '../../../directives/indeterminate.directive';

@Component({
    selector: 'sh-form-checkbox-complex-base',
    templateUrl: './form-checkbox-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormCheckboxComplexBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, ShIndeterminateDirective, AsyncPipe]
})
export class ShFormCheckboxComplexBaseComponent
    extends SingleValueFormInput<boolean | null>
    implements OnInit, OnDestroy, AfterViewInit
{
    public readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    control: UntypedFormControl;

    @Input() id!: string;

    @Input() invalid = false;

    @Input() describeLabelId = '';

    @Input() label = '';

    @Input() hideInnerLabel: boolean = false;

    @ViewChild('checkboxEl') checkboxEl!: ElementRef;

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);

        this.control = new UntypedFormControl();
    }

    ngOnInit(): void {
        this.initPropagation();
    }

    ngAfterViewInit(): void {
        this.initCheckboxListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.indeterminate$.complete();
    }

    initPropagation(): void {
        combineLatest([this.control.valueChanges, this.indeterminate$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([value, indeterminate]) => {
                if (indeterminate) {
                    return this.propagateChange(new FormValue(false, FormValueState.INDETERMINATE));
                }

                if (value !== null) {
                    return this.propagateChange(new FormValue(value, FormValueState.SET));
                } else {
                    return this.propagateChange(new FormValue(false, FormValueState.SET));
                }
            });
    }

    initCheckboxListener(): void {
        const changes$: Observable<any> = fromEvent(this.checkboxEl.nativeElement, 'change').pipe();

        const firstChange$: Observable<any> = changes$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChange$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));
    }

    public unset(): void {
        this.indeterminate$.next(false);

        this.control.setValue(false);
    }

    public set(value: boolean | null): void {
        if (!isBoolean(value) || value === null) {
            console.error('ShFormCheckboxComplexBaseComponent:: FormValue does not contain a boolean', value);

            this.unset();

            return;
        }

        this.indeterminate$.next(false);

        this.control.setValue(!!value);
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);

        this.control.setValue(false);
    }

    protected override propagateDisabledState(state: boolean) {
        if (state && this.control.enabled) {
            this.control.disable();
        } else if (!state && this.control.disabled) {
            this.control.enable();
        }
    }
}
